import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
} from "@chakra-ui/react";
import { ReactNode, useRef, useState } from "react";
import {
  UseFormReturn,
  DefaultValues,
  FieldValues,
  Resolver,
  SubmitHandler,
} from "react-hook-form";
import Form from "./Form";
import { Button } from "./Button";
import ConfirmDialog from "../ConfirmDialog";

interface DrawerFormProps<FormValues extends FieldValues> {
  children: (methods: UseFormReturn<FormValues>) => ReactNode;
  title: string;
  isOpen: boolean;
  isSubmitting: boolean;
  onSubmit: SubmitHandler<FormValues>;
  onClose: () => void;
  resolver: Resolver<FormValues>;
  defaultValues?: DefaultValues<FormValues>;
  btSaveId?: string;
  btCloseId?: string;
  formId?: string;
  shouldAskForSaveConfirmation?: boolean;
  saveConfirmationTitle?: string;
  saveConfirmationMessage?: string;
  saveConfirmationText?: string;
  saveCancelText?: string;
}

export default function DrawerForm<FormValues extends FieldValues>({
  children,
  title,
  isOpen,
  isSubmitting,
  onSubmit,
  onClose,
  resolver,
  defaultValues,
  btSaveId,
  btCloseId,
  formId = "hook-form",
  shouldAskForSaveConfirmation = false,
  saveConfirmationTitle,
  saveConfirmationMessage,
  saveConfirmationText,
  saveCancelText,
}: DrawerFormProps<FormValues>) {
  const formRef = useRef<HTMLFormElement | null>(null);
  const [isSaveConfirmationOpen, setIsSaveConfirmationOpen] = useState(false);

  const confirmationTitle = saveConfirmationTitle ?? "Confirm save";
  const confirmationMessage =
    saveConfirmationMessage ?? "Are you sure you want to save these changes?";
  const confirmationText = saveConfirmationText ?? "Save";
  const cancelText = saveCancelText ?? "Cancel";

  function handleSaveClick() {
    if (shouldAskForSaveConfirmation) {
      setIsSaveConfirmationOpen(true);
      return;
    }

    formRef.current?.requestSubmit();
  }

  function handleSaveConfirm() {
    setIsSaveConfirmationOpen(false);
    formRef.current?.requestSubmit();
  }

  function handleSaveConfirmationClose() {
    setIsSaveConfirmationOpen(false);
  }

  return (
    <>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size={"lg"}>
        <DrawerOverlay />
        <DrawerContent motionProps={{ initial: "none", exit: "none" }}>
          <DrawerCloseButton />
          <DrawerHeader>{title}</DrawerHeader>
          <DrawerBody>
            <Form<FormValues>
              defaultValues={defaultValues}
              resolver={resolver}
              onSubmit={onSubmit}
              formId={formId}
              formRef={formRef}
            >
              {methods => children(methods)}
            </Form>
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px">
            <Button
              onClick={handleSaveClick}
              mr={3}
              isLoading={isSubmitting}
              type="button"
              data-testid={btSaveId}
            >
              Save
            </Button>
            <Button
              _hover={{ bg: "#CCC" }}
              background={"#BBB"}
              type="button"
              variant="outline"
              mr={3}
              onClick={onClose}
              data-testid={btCloseId}
            >
              Close
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {shouldAskForSaveConfirmation && (
        <ConfirmDialog
          isOpen={isSaveConfirmationOpen}
          onClose={handleSaveConfirmationClose}
          onConfirm={handleSaveConfirm}
          title={confirmationTitle}
          message={confirmationMessage}
          confirmText={confirmationText}
          cancelText={cancelText}
        />
      )}
    </>
  );
}
